// src/pages/TruckHDWheels.jsx
import React from "react";
import { Link } from "react-router-dom";

/**
 * Edit ONLY this list to control the 2 tiles.
 * - title: shows in the black footer bar
 * - subtitle: small line under the title (like your monoblock page)
 * - img: image path in /public/assets/...
 * - to: route you want it to go to
 */
const TRUCK_HD_STYLE_TILES = [
  {
    title: "TS1",
    subtitle: "A multi-layer forged mesh design built for maximum strength and deep visual complexity.",
    img: "/assets/truckhd-style-1.jpg",
    to: "/wheels/truck-hd/ts1",
  },
  {
    title: "TS2",
    subtitle: "A bold 6 split-spoke forged design.",
    img: "/assets/truckhd-style-2.jpg",
    to: "/wheels/truck-hd/ts2",
  },
{
    title: "TS3",
    subtitle: "A clean, modern forged multi Y-spoke design that balances strength, and a refined aggressive stance.",
    img: "/assets/truckhd-style-3.jpg",
    to: "/wheels/truck-hd/ts3",
  },
{
    title: "TS4",
    subtitle: "An aggressive forged mesh design with deep profiles and intricate detailing.",
    img: "/assets/truckhd-style-4.jpg",
    to: "/wheels/truck-hd/ts4",
  },

];

export default function TruckHDWheels() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Truck HD Wheels
        </h1>
        <p className="mt-4 text-neutral-600">
          Heavy-duty forged wheel program for trucks and SUVs. Built for towing,
          lifted applications, and daily work use with proper load ratings,
          aggressive fitments, and signature Unlimited Levels finishes.
        </p>

        {/* CTA */}
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/build-your-custom-wheels"
            className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold bg-black text-white hover:bg-neutral-900 w-full sm:w-auto"
          >
            Request a Truck HD Wheel Quote
          </Link>
          <a
            href="#truck-hd-details"
            className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold border border-neutral-300 hover:bg-neutral-50 w-full sm:w-auto"
          >
            View Program Details
          </a>
        </div>

        <p className="mt-3 text-xs text-neutral-500">
          Tell us your truck, suspension setup, and target fitment. We’ll spec
          widths, offsets, and tires for you.
        </p>
      </div>

      {/* Hero */}
      <div className="mt-10 flex justify-center">
        <div className="w-full max-w-5xl rounded-3xl overflow-hidden border border-neutral-200 bg-neutral-50">
          <img
            src="/assets/truckhd.jpg"
            alt="Unlimited Levels Truck HD Wheels"
            className="w-full h-[260px] sm:h-[420px] object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* ✅ NEW: Two “style” tiles like your Monoblock page */}
      <div className="mt-12">
        <h2 className="text-lg font-semibold tracking-tight mb-4">
          Choose Your Style
        </h2>

        <div className="grid gap-6 md:grid-cols-2">
          {TRUCK_HD_STYLE_TILES.map((t) => (
            <Link
              key={t.title}
              to={t.to}
              className="group block rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-sm hover:shadow-lg transition"
              aria-label={`Open ${t.title}`}
            >
              {/* Image area */}
              <div className="bg-neutral-100">
                <img
                  src={t.img}
                  alt={t.title}
                  className="w-full h-72 sm:h-80 object-contain bg-white group-hover:scale-[1.02] transition-transform"
                  loading="lazy"
                  onError={(e) => {
                    // fallback so missing images don’t make the page look like a failed science experiment
                    e.currentTarget.src = "/assets/truckhd.jpg";
                  }}
                />
              </div>

              {/* Footer bar (matches your screenshot style) */}
              <div className="bg-neutral-900 text-white px-5 py-4">
                <div className="text-sm font-semibold tracking-wide">
                  {t.title}
                </div>
                <div className="text-xs text-white/80 mt-1">
                  {t.subtitle}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-3 text-xs text-neutral-500">
          Want a different look? Tell us your tire size, lift height, and the
          stance you want. We’ll spec it correctly.
        </p>
      </div>

      {/* Feature cards */}
      <div
        id="truck-hd-details"
        className="mt-12 grid gap-6 md:grid-cols-3 text-sm text-neutral-700"
      >
        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
          <h2 className="font-semibold mb-2">Forged for Trucks &amp; SUVs</h2>
          <p>
            Engineered load ratings for half-ton and heavy-duty platforms, with
            hub bores and bolt patterns tailored to your chassis.
          </p>
        </div>

        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
          <h2 className="font-semibold mb-2">Aggressive Fitments</h2>
          <p>
            Deep concave, proper poke, and lifted truck clearances. We’ll set
            offsets for your exact ride height and tire size.
          </p>
        </div>

        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
          <h2 className="font-semibold mb-2">Signature Finishes</h2>
          <p>
            Brushed, polished, tinted clears, and blackout options to match your
            build. Dual-tone and custom color matching available.
          </p>
        </div>
      </div>

      {/* Specs / Program highlights */}
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-neutral-200 bg-white p-6">
          <h3 className="text-lg font-semibold">What we need from you</h3>
          <ul className="mt-3 space-y-2 text-sm text-neutral-700 list-disc pl-5">
            <li>Year / make / model + trim (and brake package if relevant)</li>
            <li>Stock vs lifted leveling kit (height + brand if you know)</li>
            <li>Target tire size (or we can recommend)</li>
            <li>Desired stance: flush / slight poke / aggressive</li>
            <li>Finish preference: brushed / polished / blackout / custom</li>
          </ul>
          <div className="mt-5">
            <Link
              to="/build-your-custom-wheels"
              className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold bg-black text-white hover:bg-neutral-900"
            >
              Start Quote Form
            </Link>
          </div>
        </div>

        <div className="rounded-3xl border border-neutral-200 bg-white p-6">
          <h3 className="text-lg font-semibold">Typical build goals</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="font-semibold">Tow-ready strength</div>
              <p className="mt-1 text-neutral-600">
                Proper load ratings and hub-centric fitment.
              </p>
            </div>
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="font-semibold">Lifted clearance</div>
              <p className="mt-1 text-neutral-600">
                Offset + width spec’d around suspension and tire size.
              </p>
            </div>
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="font-semibold">Concave profiles</div>
              <p className="mt-1 text-neutral-600">
                Deep designs where the truck allows it.
              </p>
            </div>
            <div className="rounded-2xl border border-neutral-200 p-4">
              <div className="font-semibold">Clean stance</div>
              <p className="mt-1 text-neutral-600">
                Flush to mild poke, not cartoon clown wheels.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-14 max-w-4xl mx-auto">
        <h3 className="text-2xl font-semibold text-center">FAQ</h3>

        <div className="mt-6 space-y-4">
          <details className="group rounded-2xl border border-neutral-200 bg-white p-5">
            <summary className="cursor-pointer font-semibold">
              Do you build wheels for HD trucks (2500/3500)?
            </summary>
            <p className="mt-2 text-sm text-neutral-700">
              Yes. We spec bolt pattern, hub bore, and load rating for your exact
              platform and your suspension setup.
            </p>
          </details>

          <details className="group rounded-2xl border border-neutral-200 bg-white p-5">
            <summary className="cursor-pointer font-semibold">
              Can you match offsets to my lift and tire size?
            </summary>
            <p className="mt-2 text-sm text-neutral-700">
              That’s the whole point. Send your lift height and tire goals and
              we’ll recommend width + offset to hit your stance.
            </p>
          </details>

          <details className="group rounded-2xl border border-neutral-200 bg-white p-5">
            <summary className="cursor-pointer font-semibold">
              How long does production take?
            </summary>
            <p className="mt-2 text-sm text-neutral-700">
              Most forged sets are built to order. Timing depends on finish and
              spec, but we’ll give you an ETA with your quote.
            </p>
          </details>
        </div>
      </div>

      {/* Final CTA */}
      <div className="mt-14 flex flex-col items-center gap-3">
        <Link
          to="/build-your-custom-wheels"
          className="inline-flex items-center justify-center rounded-full px-7 py-3 text-sm font-semibold bg-black text-white hover:bg-neutral-900"
        >
          Request a Truck HD Wheel Quote
        </Link>
        <p className="text-xs text-neutral-500 text-center max-w-xl">
          Include your truck model, lift details, and your target stance. We’ll
          reply with recommended sizes, offsets, and finish options.
        </p>
      </div>
    </section>
  );
}
